import React from "react";
import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import moment from "moment";

const EtTrinityConversationsChartDemo = () => {
  // Author - Atul Pandey
  // First Created - 16 Mar 2026
  // Purpose - lightweight sample chart to preview Conversation visualization in onboarding
  // Updated - 16 Mar 2026 - mirrors conversation streak and cumulative chart style with static sample data

  const [chartTab, setChartTab] = React.useState("streak");

  const streakData = [
    { xValue: 0, xLabel: "Mon", epochMinutes: 570, createdAt: "2026-03-09T09:30:00" },
    { xValue: 1, xLabel: "Tue", epochMinutes: 690, createdAt: "2026-03-10T11:30:00" },
    { xValue: 2, xLabel: "Wed", epochMinutes: 840, createdAt: "2026-03-11T14:00:00" },
    { xValue: 3, xLabel: "Thu", epochMinutes: 630, createdAt: "2026-03-12T10:30:00" },
    { xValue: 4, xLabel: "Fri", epochMinutes: 930, createdAt: "2026-03-13T15:30:00" },
    { xValue: 5, xLabel: "Sat", epochMinutes: 720, createdAt: "2026-03-14T12:00:00" },
    { xValue: 6, xLabel: "Sun", epochMinutes: 1020, createdAt: "2026-03-15T17:00:00" },
  ];

  const dailyCountData = [
    { xValue: 0, xLabel: "Mon", dateKey: "2026-03-09", dailyCount: 1, cumulativeCount: 1 },
    { xValue: 1, xLabel: "Tue", dateKey: "2026-03-10", dailyCount: 2, cumulativeCount: 3 },
    { xValue: 2, xLabel: "Wed", dateKey: "2026-03-11", dailyCount: 1, cumulativeCount: 4 },
    { xValue: 3, xLabel: "Thu", dateKey: "2026-03-12", dailyCount: 3, cumulativeCount: 7 },
    { xValue: 4, xLabel: "Fri", dateKey: "2026-03-13", dailyCount: 2, cumulativeCount: 9 },
    { xValue: 5, xLabel: "Sat", dateKey: "2026-03-14", dailyCount: 1, cumulativeCount: 10 },
    { xValue: 6, xLabel: "Sun", dateKey: "2026-03-15", dailyCount: 2, cumulativeCount: 12 },
  ];

  return (
    <div className="mt-3 space-y-4">
      <div className="rounded border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">
            Sample Conversation Charts
          </p>
          <p className="text-xs text-gray-500">Mon to Sun sample view</p>
        </div>

        <div className="flex space-x-2 mb-3">
          <button
            onClick={() => setChartTab("streak")}
            className={`text-xs font-semibold px-3 py-1 rounded ${
              chartTab === "streak"
                ? "bg-orange-200 text-orange-800"
                : "text-gray-500"
            }`}
          >
            Streak View
          </button>

          <button
            onClick={() => setChartTab("counts")}
            className={`text-xs font-semibold px-3 py-1 rounded ${
              chartTab === "counts"
                ? "bg-orange-200 text-orange-800"
                : "text-gray-500"
            }`}
          >
            Daily & Cumulative
          </button>
        </div>

        <div className="w-full h-[240px] text-xs">
          <ResponsiveContainer>
            {chartTab === "streak" ? (
              <LineChart
                data={streakData}
                margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  type="number"
                  dataKey="xValue"
                  domain={["dataMin", "dataMax"]}
                  ticks={streakData.map((d) => d.xValue)}
                  tickFormatter={(x) =>
                    streakData.find((d) => d.xValue === x)?.xLabel || ""
                  }
                />

                <YAxis
                  type="number"
                  domain={[360, 1260]}
                  ticks={[360, 540, 720, 900, 1080, 1260]}
                  tickFormatter={(m) =>
                    moment().startOf("day").add(m, "minutes").format("h A")
                  }
                  label={{
                    value: "Time of Day",
                    angle: -90,
                    position: "insideLeft",
                    offset: -2,
                  }}
                />

                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0]?.payload;
                    const dateStr = moment(p.createdAt).format("ddd, DD MMM YYYY");
                    const timeStr = moment(p.createdAt).format("h:mm A");

                    return (
                      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                        <p className="font-semibold text-gray-700">{dateStr}</p>
                        <p className="text-sm text-gray-600">{timeStr}</p>
                      </div>
                    );
                  }}
                />

                <Line
                  type="monotone"
                  dataKey="epochMinutes"
                  stroke="#EA580C"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#EA580C" }}
                  name="Conversation"
                  connectNulls={true}
                />
              </LineChart>
            ) : (
              <LineChart
                data={dailyCountData}
                margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  type="number"
                  dataKey="xValue"
                  domain={["dataMin", "dataMax"]}
                  ticks={dailyCountData.map((d) => d.xValue)}
                  tickFormatter={(x) =>
                    dailyCountData.find((d) => d.xValue === x)?.xLabel || ""
                  }
                />

                <YAxis
                  type="number"
                  allowDecimals={false}
                  label={{
                    value: "Daily & Cumulative Count",
                    angle: -90,
                    position: "insideLeft",
                    dx: -10,
                    dy: 0,
                    style: { textAnchor: "middle" },
                  }}
                />

                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0]?.payload;
                    const dateStr = moment(p.dateKey).format("ddd, DD MMM YYYY");

                    return (
                      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                        <p className="font-semibold text-gray-700">{dateStr}</p>
                        <p className="text-sm text-gray-600">
                          Daily: {p.dailyCount}
                        </p>
                        <p className="text-sm text-gray-600">
                          Cumulative: {p.cumulativeCount}
                        </p>
                      </div>
                    );
                  }}
                />

                <Legend wrapperStyle={{ paddingTop: "20px" }} />

                <Bar
                  dataKey="dailyCount"
                  fill="#65a30d"
                  name="Daily Conversations"
                  barSize={22}
                />

                <Line
                  type="monotone"
                  dataKey="cumulativeCount"
                  stroke="#EA580C"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#EA580C" }}
                  name="Cumulative Conversations"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        This sample shows how your actual Conversation charts can appear as you build conversation rhythm and cumulative engagement over time.
      </p>
    </div>
  );
};

export default EtTrinityConversationsChartDemo;